"use client";

import Image from "next/image";
import ScrollAnimation from "./ScrollAnimation";
import { useKlaroConsent } from "@/hooks/useKlaroConsent";

function YoutubeEmbed({ src }: { src: string }) {
  const { consent, showModal } = useKlaroConsent("youtube");

  if (!consent) {
    return (
      <div className="absolute inset-0 flex flex-col items-center justify-center bg-gradient-to-br from-primary/90 to-secondary/90 text-center p-6">
        <p className="mb-4 text-textLight">YouTube-Inhalte sind deaktiviert.</p>
        <button onClick={showModal} className="btn-primary text-sm py-2 px-4">Aktivieren</button>
      </div>
    );
  }

  return (
    <iframe
      src={src}
      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
      allowFullScreen
      className="absolute inset-0 w-full h-full"
    ></iframe>
  );
}

function SpotifyEmbed({ src, height = 80 }: { src: string, height?: number }) {
  const { consent, showModal } = useKlaroConsent("spotify");

  if (!consent) {
    return (
      <div className="flex flex-col items-center justify-center bg-gradient-to-br from-primary/90 to-secondary/90 text-center p-4 rounded-xl h-full w-full">
        <p className="mb-2 text-textLight text-sm">Spotify deaktiviert.</p>
        <button onClick={showModal} className="btn-primary text-xs py-1 px-3">Aktivieren</button>
      </div>
    );
  }

  return (
    <iframe
      src={src}
      width="100%"
      height={height}
      allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
      loading="lazy"
      className="rounded-xl"
    ></iframe>
  );
}

export default function MusicSection() {
  return (
    <section className="section-block music-section text-center" id="musik">
      <ScrollAnimation animation="fade-in">
        <h2>Unsere Musik</h2>
      </ScrollAnimation>

      <div className="videos-grid grid grid-cols-1 md:grid-cols-2 gap-10 max-w-6xl mx-auto mb-20 px-5">
        <ScrollAnimation animation="scale-up" className="col-span-1 md:col-span-2">
          <div className="video-container relative aspect-video rounded-2xl overflow-hidden shadow-2xl border-2 border-secondary">
            <YoutubeEmbed src="https://www.youtube-nocookie.com/embed/bMd_6NOPBlM" />
          </div>
        </ScrollAnimation>
        
        <ScrollAnimation animation="scale-up" delay={200}>
          <div className="video-container relative aspect-video rounded-2xl overflow-hidden shadow-2xl border-2 border-secondary">
            <YoutubeEmbed src="https://www.youtube-nocookie.com/embed/OZPxwopYT1s?si=34u2lJLfCEAcIVTo" />
          </div>
        </ScrollAnimation>

        <ScrollAnimation animation="scale-up" delay={400}>
          <div className="video-container relative aspect-video rounded-2xl overflow-hidden shadow-2xl border-2 border-secondary">
            <YoutubeEmbed src="https://www.youtube-nocookie.com/embed/kjW5N20AZLc" />
          </div>
        </ScrollAnimation>
      </div>

      <ScrollAnimation animation="fade-in">
        <div className="max-w-4xl mx-auto px-4 mb-20">
          <div className="music-description bg-gradient-to-br from-primary/40 to-black/60 backdrop-blur-md border border-white/10 p-8 md:p-12 rounded-2xl shadow-2xl relative overflow-hidden group">
            {/* Subtle decorative element */}
            <div className="absolute -top-24 -right-24 w-48 h-48 bg-accent/5 rounded-full blur-3xl group-hover:bg-accent/10 transition-colors duration-700" />
            
            <div className="relative z-10 space-y-6 text-lg md:text-xl leading-relaxed font-serif text-gray-200">
              <p>
                Seit Sommer 2024 arbeiten wir kontinuierlich an neuer Musik, die sich mit jedem Song weiterentwickelt. Unsere Klanglandschaften sind groß, detailreich und voller Kontraste – von treibenden Riffs bis zu epischen Orchesterpassagen.
              </p>
              
              <p>
                Unsere Musik erzählt Geschichten. Jedes Lied eröffnet neue Perspektiven auf alte und neue Erzählungen. Wir möchten Welten erschaffen, in denen sich unsere Hörer:innen verlieren können oder vielleicht sogar selbst wiederfinden.
              </p>
              
              <p className="pt-4 border-t border-white/5">
                Im Frühjahr 2025 erschien unsere Single <span className="text-accent italic font-medium">&quot;RONIN&quot;</span> mit einem selbstproduzierten Musikvideo. Auch unsere aktuelle Single <span className="text-accent italic font-medium">&quot;Morpheus&quot;</span> ist mit einem Musikvideo veröffentlicht worden, das für uns neue visuelle Maßstäbe setzt.
              </p>
            </div>

            {/* Bottom accent line */}
            <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-accent/30 to-transparent" />
          </div>
        </div>
      </ScrollAnimation>

      {/* Album: Morpheus */}
      <div className="album-section flex flex-wrap justify-center items-start gap-16 max-w-6xl mx-auto mb-20 px-5">
        <ScrollAnimation animation="fade-in">
          <div className="album-cover w-[400px] h-[400px] rounded-3xl overflow-hidden shadow-2xl border-4 border-primary hover:scale-105 hover:border-accent transition-all">
            <Image src="/img/morpheus_cover.png" alt="Morpheus Cover" width={400} height={400} className="w-full h-full object-cover" />
          </div>
        </ScrollAnimation>
        <ScrollAnimation animation="slide-left" className="flex-1 min-w-[400px]">
          <div className="tracklist-container bg-gradient-to-br from-primary/80 to-secondary/80 backdrop-blur-xl rounded-2xl p-8 shadow-2xl border border-accent/20">
            <h3 className="text-3xl mb-6 shadow-accent/50">Morpheus</h3>
            <div className="single-track-container h-[152px]">
              <SpotifyEmbed src="https://open.spotify.com/embed/track/67AyylEZfF8e7g3nM1hggR?utm_source=generator" height={152} />
            </div>
          </div>
        </ScrollAnimation>
      </div>

      {/* Album: Ex Libris */}
      <div className="album-section flex flex-wrap justify-center items-start gap-16 max-w-6xl mx-auto px-5">
        <ScrollAnimation animation="fade-in">
          <div className="album-cover w-[400px] h-[400px] rounded-3xl overflow-hidden shadow-2xl border-4 border-primary hover:scale-105 hover:border-accent transition-all">
            <Image src="/img/cover.png" alt="Ex Libris Cover" width={400} height={400} className="w-full h-full object-cover" />
          </div>
        </ScrollAnimation>
        <ScrollAnimation animation="slide-left" className="flex-1 min-w-[400px]">
          <div className="tracklist-container bg-gradient-to-br from-primary/80 to-secondary/80 backdrop-blur-xl rounded-2xl p-8 shadow-2xl border border-accent/20">
            <h3 className="text-3xl mb-6 shadow-accent/50">Chapter I: Ex Libris</h3>
                      <div className="w-full text-left font-serif flex flex-col gap-6">
                        {[
                          { title: "Ex Libris", id: "2HVI7FaeQhfvhS34aIsqTv" },
                          { title: "Final Frontier", id: "4m41XT12ca25xHpn02UnxN" },
                          { title: "Lieutenant Jackson", id: "146TZCBa9yG68A1HKyd3GW" },
                          { title: "The Good Deed", id: "4GDhrImtZ125yHfqLzUn7D" },
                          { title: "The Tale of Silverfox", id: "0L4yFgbZjAO4abrXVwCwaE" },
                          { title: "Berggeist", id: "3ifbsx6MY0fktbu4HJKATy" },
                          { title: "Symphony of Winds", id: "1mcHTX09ao9UkTsl720uHw" },
                          { title: "RONIN", id: "7amacdVwAX0uiByAaNV47G" }
                        ].map((track, i) => (
                          <div key={i} className="flex flex-col md:flex-row md:items-center border-b border-accent/10 pb-6 last:border-0 last:pb-0">
                            <div className="font-bold text-accent text-lg mb-3 md:mb-0 md:w-1/3 md:pr-4">{track.title}</div>
                            <div className="w-full md:w-2/3 h-[80px]">
                              <SpotifyEmbed src={`https://open.spotify.com/embed/track/${track.id}?utm_source=generator`} />
                            </div>
                          </div>
                        ))}
                      </div>
            
          </div>
        </ScrollAnimation>
      </div>
    </section>
  );
}
